import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, FileImage, FileArchive, FileAudio, FileType, File } from 'lucide-react';

const extIcon = (name) => {
  const n = (name || '').toLowerCase();
  if (n.endsWith('.pdf')) return FileType;
  if (n.endsWith('.png') || n.endsWith('.jpg') || n.endsWith('.jpeg') || n.endsWith('.gif')) return FileImage;
  if (n.endsWith('.zip') || n.endsWith('.rar') || n.endsWith('.7z')) return FileArchive;
  if (n.endsWith('.mp3') || n.endsWith('.wav') || n.endsWith('.m4a')) return FileAudio;
  if (n.endsWith('.doc') || n.endsWith('.docx') || n.endsWith('.txt')) return FileText;
  return File;
};

const parseSizeMB = (sizeStr) => {
  if (!sizeStr) return 0;
  const m = /([\d\.]+)\s*MB/i.exec(sizeStr);
  return m ? parseFloat(m[1]) : 0;
};

// Compute absolute positions for widget-like tiles (square, tall, wide, large)
function computeWidgetLayout({ items, columns, gap, containerWidth, pattern }) {
  const colW = columns > 0 ? (containerWidth - (columns - 1) * gap) / columns : 0;
  const cell = colW; // square unit
  const occ = []; // rows x cols boolean occupancy

  const ensureRows = (rows) => {
    while (occ.length < rows) occ.push(new Array(columns).fill(false));
  };

  const fits = (r, c, w, h) => {
    for (let y = 0; y < h; y++) {
      const rr = r + y; ensureRows(rr + 1);
      for (let x = 0; x < w; x++) {
        if (c + x >= columns) return false;
        if (occ[rr][c + x]) return false;
      }
    }
    return true;
  };

  const mark = (r, c, w, h) => {
    for (let y = 0; y < h; y++) {
      const rr = r + y; ensureRows(rr + 1);
      for (let x = 0; x < w; x++) occ[rr][c + x] = true;
    }
  };

  const results = [];
  const shapes = { square: [1,1], tall: [1,2], wide: [2,1], large: [2,2] };

  items.forEach((item, i) => {
    const shapeName = item.shape || pattern[i % pattern.length] || 'square';
    const [w, h] = shapes[shapeName] || [1,1];
    // scan rows to place
    let placed = false; let r = 0;
    while (!placed) {
      ensureRows(r + h);
      for (let c = 0; c <= columns - Math.min(w, columns); c++) {
        if (fits(r, c, Math.min(w, columns), h)) {
          const W = Math.min(w, columns);
          mark(r, c, W, h);
          const x = Math.round(c * (cell + gap));
          const y = Math.round(r * (cell + gap));
          const width = Math.round(W * cell + (W - 1) * gap);
          const height = Math.round(h * cell + (h - 1) * gap);
          results.push({ index: i, x, y, width, height, key: item.id ?? i });
          placed = true; break;
        }
      }
      if (!placed) r += 1;
    }
  });

  const usedRows = occ.length;
  const totalHeight = Math.max(0, usedRows * (cell + gap) - gap);
  return { cell, colW: cell, positions: results, totalHeight };
}

export default function IOSWidgetGrid({
  items = [],
  sort = 'recent', // 'recent' | 'name' | 'size' | 'type'
  density = 'comfortable', // affects gap
  columns = 2,
  pattern = ['tall', 'square', 'large', 'wide', 'square', 'tall'],
  onItemClick,
}) {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sorted = useMemo(() => {
    const arr = [...items];
    switch (sort) {
      case 'name': arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      case 'size': arr.sort((a, b) => parseSizeMB(b.size) - parseSizeMB(a.size)); break;
      case 'type': arr.sort((a, b) => (a.type || '').localeCompare(b.type || '')); break;
      case 'recent':
      default: arr.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    }
    return arr;
  }, [items, sort]);

  const gap = density === 'compact' ? 8 : 12;
  const { positions, totalHeight } = useMemo(() => {
    if (!width) return { positions: [], totalHeight: 0 };
    return computeWidgetLayout({ items: sorted, columns, gap, containerWidth: width, pattern });
  }, [sorted, columns, gap, width, pattern]);

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.04 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  const renderTile = (item) => {
    const Icon = extIcon(item.name);
    return (
      <div className="w-full h-full rounded-3xl overflow-hidden bg-white/80 dark:bg-slate-800/70 backdrop-blur border border-white/20 dark:border-slate-700/40 shadow-sm">
        <div className="p-4 h-full flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl grid place-items-center shadow-sm" style={{ background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)' }}>
              <Icon className="text-indigo-500" size={22} />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.type || 'file'} • {item.size}</div>
            </div>
          </div>
          {item.date && <div className="text-[11px] mt-2 text-gray-400 dark:text-gray-500">{new Date(item.date).toLocaleDateString()}</div>}
        </div>
      </div>
    );
  };

  return (
    <motion.div ref={ref} className="relative w-full" variants={containerVariants} initial="hidden" animate="visible">
      <div style={{ position: 'relative', height: totalHeight }}>
        {positions.map((p) => {
          const item = sorted[p.index];
          return (
            <motion.button
              key={p.key}
              className="absolute focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-3xl"
              style={{ left: p.x, top: p.y, width: p.width, height: p.height }}
              variants={itemVariants}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => onItemClick && onItemClick(item)}
            >
              {renderTile(item)}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
